import React, { useMemo, useState } from 'react';
import './DexaResultsDashboard.scss';

type AnyRecord = Record<string, any>;

interface DexaResultsDashboardProps {
  data: Record<string, unknown> | null;
}

interface BmiBand {
  label: string;
  range_low: number | null;
  range_high: number | null;
  caveat_note?: string | null;
}

const DEFAULT_BMI_BANDS: BmiBand[] = [
  { label: 'Underweight', range_low: null, range_high: 18.4, caveat_note: 'BMI cannot differentiate between fat and muscle mass' },
  { label: 'Normal', range_low: 18.5, range_high: 24.9, caveat_note: 'BMI cannot differentiate between fat and muscle mass' },
  { label: 'Overweight', range_low: 25.0, range_high: 29.9, caveat_note: 'BMI cannot differentiate between fat and muscle mass' },
  { label: 'Obese', range_low: 30.0, range_high: 34.9, caveat_note: 'BMI cannot differentiate between fat and muscle mass' },
  { label: 'Severely Obese', range_low: 35.0, range_high: 39.9, caveat_note: 'BMI cannot differentiate between fat and muscle mass' },
  { label: 'Morbidly Obese', range_low: 40.0, range_high: null, caveat_note: 'BMI cannot differentiate between fat and muscle mass' }
];

const BONE_REGION_ORDER = [
  'Left Arm', 'Right Arm', 'Left Ribs', 'Right Ribs', 'Thoracic Spine', 'Lumbar Spine',
  'Pelvis', 'Left Leg', 'Right Leg', 'Subtotal', 'Total'
];

const COMPOSITION_REGION_ORDER = [
  ['l_arm', 'Left Arm'],
  ['r_arm', 'Right Arm'],
  ['trunk', 'Trunk'],
  ['l_leg', 'Left Leg'],
  ['r_leg', 'Right Leg'],
  ['subtotal', 'Subtotal']
] as const;

const BMI_GAUGE_MIN = 15;
const BMI_GAUGE_MAX = 50;

const BMI_BAND_COLORS: Record<string, string> = {
  Underweight: '#64b5f6',
  Normal: '#81c784',
  Overweight: '#ffd54f',
  Obese: '#ffb74d',
  'Severely Obese': '#e57373',
  'Morbidly Obese': '#c62828'
};

const formatNumber = (value: unknown, digits = 1): string | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toFixed(digits).replace(/\.0$/, '');
};

const formatSigned = (value: unknown, digits = 2): string | null => {
  const formatted = formatNumber(value, digits);
  if (formatted === null) return null;
  const n = Number(value);
  if (Number.isNaN(n) || n <= 0) return formatted;
  return `+${formatted}`;
};

const presentNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(n) ? null : n;
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const markerPercent = (value: number, min: number, max: number) =>
  `${clamp(((value - min) / (max - min)) * 100, 0, 100)}%`;

const classificationTone = (label?: string | null): 'normal' | 'osteopenia' | 'osteoporosis' | 'neutral' => {
  const text = (label || '').toLowerCase();
  if (text.includes('osteoporosis')) return 'osteoporosis';
  if (text.includes('osteopenia')) return 'osteopenia';
  if (text.includes('normal')) return 'normal';
  return 'neutral';
};

const bmiTone = (label?: string | null): string => {
  const text = (label || '').toLowerCase();
  if (text.includes('underweight')) return 'underweight';
  if (text.includes('morbidly')) return 'morbidly-obese';
  if (text.includes('severely')) return 'severely-obese';
  if (text.includes('obese')) return 'obese';
  if (text.includes('overweight')) return 'overweight';
  if (text.includes('normal')) return 'normal';
  return 'neutral';
};

const tScoreClassFromValue = (tScore?: number | null): string | null => {
  if (tScore === null || tScore === undefined || Number.isNaN(tScore)) return null;
  if (tScore <= -2.5) return 'Osteoporosis';
  if (tScore < -1) return 'Osteopenia';
  return 'Normal';
};

const normalizeRegionName = (region: string) => region.trim().toLowerCase().replace(/\s+/g, ' ');

const isHeadRegion = (region: string) => normalizeRegionName(region) === 'head';

const isSubtotalRegion = (region: string) => {
  const name = normalizeRegionName(region);
  return name === 'subtotal' || name.includes('subtotal');
};

const isTotalRegion = (region: string) => {
  const name = normalizeRegionName(region);
  if (isSubtotalRegion(region)) return false;
  return name === 'total' || name === 'wb total' || name === 'whole body' || name.endsWith(' total');
};

const boneRegionRank = (region: string) => {
  if (isTotalRegion(region)) return 10_000;
  if (isSubtotalRegion(region)) return 9_999;
  const index = BONE_REGION_ORDER.findIndex(
    (r) => normalizeRegionName(r) === normalizeRegionName(region)
  );
  return index === -1 ? 500 : index;
};

const buildBmiGaugeSegments = (bands: BmiBand[]) =>
  bands.map((band) => ({
    label: band.label,
    from: band.range_low ?? BMI_GAUGE_MIN,
    to: band.range_high ?? BMI_GAUGE_MAX,
    color: BMI_BAND_COLORS[band.label] || '#e5e7eb'
  }));

const collectBoneRegions = (bone: AnyRecord): AnyRecord[] => {
  const byKey = new Map<string, AnyRecord>();

  const addRegion = (row: AnyRecord) => {
    const region = row?.region;
    if (!region || isHeadRegion(String(region))) return;

    const key = normalizeRegionName(String(region));
    const existing = byKey.get(key);
    const merged = existing ? { ...existing, ...row } : { ...row };
    byKey.set(key, merged);
  };

  if (Array.isArray(bone.regions)) {
    bone.regions.forEach(addRegion);
  }

  if (Array.isArray(bone.scans)) {
    bone.scans.forEach((scan: AnyRecord) => {
      if (Array.isArray(scan.regions)) {
        scan.regions.forEach(addRegion);
      }
    });
  }

  const regions = Array.from(byKey.values());
  regions.sort((a, b) => {
    const aRank = boneRegionRank(String(a.region));
    const bRank = boneRegionRank(String(b.region));
    return aRank - bRank || String(a.region).localeCompare(String(b.region));
  });

  return regions.filter(
    (row) =>
      presentNumber(row.area_cm2) != null ||
      presentNumber(row.bmc_g) != null ||
      presentNumber(row.bmd_g_cm2) != null
  );
};

const ScoreGauge: React.FC<{
  value: number | null;
  min: number;
  max: number;
  segments: Array<{ label: string; from: number; to: number; color: string }>;
  tickLabels?: Array<{ value: number; label: string }>;
}> = ({ value, min, max, segments, tickLabels }) => {
  const widthFor = (from: number, to: number) => `${((to - from) / (max - min)) * 100}%`;

  return (
    <div className='dexa-gauge'>
      <div className='dexa-gauge-track'>
        {segments.map((segment) => (
          <div
            key={segment.label}
            className='dexa-gauge-segment'
            style={{ width: widthFor(segment.from, segment.to), background: segment.color }}
            title={segment.label}
          />
        ))}
        {value !== null && !Number.isNaN(value) && (
          <div className='dexa-gauge-marker' style={{ left: markerPercent(value, min, max) }} />
        )}
      </div>
      <div className='dexa-gauge-labels'>
        {segments.map((segment) => (
          <span key={segment.label} style={{ width: widthFor(segment.from, segment.to) }}>
            {segment.label}
          </span>
        ))}
      </div>
      {tickLabels && (
        <div className='dexa-gauge-ticks'>
          {tickLabels.map((tick) => (
            <span key={tick.label} style={{ left: markerPercent(tick.value, min, max) }}>
              {tick.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const MiniSiteBar: React.FC<{ value: number | null }> = ({ value }) => {
  const min = -5;
  const max = 1;
  return (
    <div className='dexa-mini-bar'>
      <div className='dexa-mini-bar-track'>
        <span className='seg osteoporosis' />
        <span className='seg osteopenia' />
        <span className='seg normal' />
        {value !== null && !Number.isNaN(value) && (
          <i style={{ left: markerPercent(value, min, max) }} />
        )}
      </div>
    </div>
  );
};

interface ExtractedImage {
  width?: number;
  height?: number;
  mime_type?: string;
  data_url: string;
}

const asExtractedImage = (value: unknown): ExtractedImage | null => {
  if (!value || typeof value !== 'object') return null;
  const img = value as AnyRecord;
  if (typeof img.data_url !== 'string' || img.data_url.length === 0) return null;
  return img as ExtractedImage;
};

const DexaResultsDashboard: React.FC<DexaResultsDashboardProps> = ({ data }) => {
  const [sitesOpen, setSitesOpen] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [boneRegionsOpen, setBoneRegionsOpen] = useState(true);
  const [compositionOpen, setCompositionOpen] = useState(true);

  const { screens, patient, scanMeta, heatmapImage, bmdImage, fmiImage, leanVsFatImage } = useMemo(() => {
    const raw = (data || {}) as AnyRecord;
    const screensData = (raw.screens || raw) as AnyRecord;
    const imageOf = (...values: unknown[]) => {
      for (const value of values) {
        const image = asExtractedImage(value);
        if (image) return image;
      }
      return null;
    };

    return {
      screens: screensData,
      patient: (raw.patient || {}) as AnyRecord,
      scanMeta: (raw.scan || {}) as AnyRecord,
      heatmapImage: imageOf(raw.heatmap_image, screensData.heatmap_image),
      bmdImage: imageOf(raw.bmd_image, screensData.bmd_image),
      fmiImage: imageOf(raw.fat_mass_index_image, screensData.fat_mass_index_image, screensData.fmi_image),
      leanVsFatImage: imageOf(
        raw.lean_vs_fat_image,
        screensData.lean_vs_fat_image,
        screensData.lean_vs_fat_by_region_image
      )
    };
  }, [data]);

  const bone = useMemo(() => (screens.bone_density || {}) as AnyRecord, [screens]);
  const body = (screens.body_composition || {}) as AnyRecord;
  const muscle = (screens.muscle_mass || {}) as AnyRecord;
  const fmi = (screens.fat_mass_index || {}) as AnyRecord;
  const visceral = (screens.visceral_fat || {}) as AnyRecord;
  const regionComposition = useMemo(
    () => (screens.lean_vs_fat_by_region || {}) as AnyRecord,
    [screens]
  );
  const regionFatPercent = useMemo(
    () => (screens.region_fat_percent || {}) as AnyRecord,
    [screens]
  );
  const symmetry = (screens.muscle_symmetry || {}) as AnyRecord;
  const summary = (screens.summary || {}) as AnyRecord;

  const bmiValue = presentNumber(summary.bmi ?? body.bmi ?? patient.bmi);
  const bmiClassification =
    summary.bmi_classification || body.bmi_classification || patient.bmi_classification || null;
  const bmiBands: BmiBand[] = Array.isArray(summary.bmi_classification_bands)
    ? summary.bmi_classification_bands
    : Array.isArray(body.bmi_classification_bands)
      ? body.bmi_classification_bands
      : Array.isArray(patient.bmi_classification_bands)
        ? patient.bmi_classification_bands
        : DEFAULT_BMI_BANDS;
  const bmiCaveat = bmiBands.find((b) => b.label === bmiClassification)?.caveat_note || bmiBands[0]?.caveat_note;

  const tScore = bone.t_score != null ? Number(bone.t_score) : null;
  const zScore = bone.z_score != null ? Number(bone.z_score) : null;
  const tClass = bone.t_score_classification || tScoreClassFromValue(tScore);
  const sites: AnyRecord[] = Array.isArray(bone.results_by_site) ? bone.results_by_site : [];
  const boneRegions = useMemo(() => collectBoneRegions(bone), [bone]);

  const compositionTableRows = useMemo(() => {
    return COMPOSITION_REGION_ORDER.map(([key, label]) => {
      const row = regionComposition?.[key] || {};
      const leanMass = presentNumber(row.lean_mass_kg);
      const fatMass = presentNumber(row.fat_mass_kg);
      const leanPercent = presentNumber(row.lean_percent);
      const fatPercent = presentNumber(row.fat_percent) ?? presentNumber(regionFatPercent?.[key]);
      const boneMass = presentNumber(row.bone_mass_g);
      const totalMass = presentNumber(row.total_mass_kg);

      if (
        leanMass == null &&
        fatMass == null &&
        leanPercent == null &&
        fatPercent == null &&
        boneMass == null &&
        totalMass == null
      ) {
        return null;
      }

      return { key, label, leanMass, fatMass, leanPercent, fatPercent, boneMass, totalMass };
    }).filter(Boolean) as Array<{
      key: string;
      label: string;
      leanMass: number | null;
      fatMass: number | null;
      leanPercent: number | null;
      fatPercent: number | null;
      boneMass: number | null;
      totalMass: number | null;
    }>;
  }, [regionComposition, regionFatPercent]);

  const regionalFatRows = useMemo(() => {
    const keys = [
      ['l_arm', 'L Arm'],
      ['r_arm', 'R Arm'],
      ['trunk', 'Trunk'],
      ['l_leg', 'L Leg'],
      ['r_leg', 'R Leg'],
      ['arms', 'Arms'],
      ['legs', 'Legs']
    ] as const;

    return keys
      .map(([key, label]) => {
        const value = presentNumber(regionFatPercent?.[key]);
        return value != null ? { key, label, value } : null;
      })
      .filter(Boolean) as Array<{ key: string; label: string; value: number }>;
  }, [regionFatPercent]);

  const regionRows = useMemo(() => {
    const order = [
      ['trunk', 'Trunk'],
      ['l_leg', 'L Leg'],
      ['r_leg', 'R Leg'],
      ['l_arm', 'L Arm'],
      ['r_arm', 'R Arm']
    ] as const;

    return order
      .map(([key, label]) => {
        const row = regionComposition?.[key] || {};
        const lean = row.lean_mass_kg != null ? Number(row.lean_mass_kg) : null;
        const fat = row.fat_mass_kg != null ? Number(row.fat_mass_kg) : null;
        if (lean === null && fat === null) return null;
        return { key, label, lean: lean || 0, fat: fat || 0 };
      })
      .filter(Boolean) as Array<{ key: string; label: string; lean: number; fat: number }>;
  }, [regionComposition]);

  const maxRegionTotal = Math.max(40, ...regionRows.map((r) => r.lean + r.fat), 1);

  const armsLeft = symmetry?.arms?.left_kg != null ? Number(symmetry.arms.left_kg) : null;
  const armsRight = symmetry?.arms?.right_kg != null ? Number(symmetry.arms.right_kg) : null;
  const legsLeft = symmetry?.legs?.left_kg != null ? Number(symmetry.legs.left_kg) : null;
  const legsRight = symmetry?.legs?.right_kg != null ? Number(symmetry.legs.right_kg) : null;
  const maxLimb = Math.max(armsLeft || 0, armsRight || 0, legsLeft || 0, legsRight || 0, 1);

  const fmiValue = fmi.fmi != null ? Number(fmi.fmi) : null;
  const visceralArea = presentNumber(visceral.area_cm2 ?? body.visceral_fat_cm2);
  const visceralVolume = presentNumber(visceral.volume_cm3 ?? body.visceral_fat_volume_cm3);
  const visceralMass = presentNumber(visceral.mass_g ?? body.visceral_fat_mass_g);
  const visceralValue =
    presentNumber(visceral.display_value) ?? visceralArea ?? visceralVolume;
  const visceralUnit =
    visceral.display_unit ||
    (visceralArea != null ? 'cm²' : visceralVolume != null ? 'cm³' : null);
  const visceralTarget = presentNumber(visceral.target_cm2 ?? body.visceral_fat_target_cm2) ?? 100;
  const hasVisceralData =
    visceralValue != null || visceralMass != null || visceral.finding || visceralArea != null;

  const scanDetails = {
    facility: summary.facility || scanMeta.facility,
    machine: summary.machine || scanMeta.machine,
    software: summary.software || scanMeta.software,
    scanId: summary.scan_id || scanMeta.scan_id,
    referencePopulation: summary.reference_population || scanMeta.reference_population,
    heightCm: presentNumber(summary.height_cm ?? patient.height_cm),
    weightKg: presentNumber(summary.weight_kg ?? patient.weight_kg),
    ethnicity: summary.ethnicity || patient.ethnicity,
    menopausalStatus: summary.menopausal_status || patient.menopausal_status,
    rmrKcal: presentNumber(summary.rmr_kcal)
  };

  const hasScanDetails = Object.values(scanDetails).some((v) => v != null && v !== '');

  const hasResultsContent =
    hasScanDetails ||
    bmiValue !== null ||
    tScore !== null ||
    zScore !== null ||
    sites.length > 0 ||
    boneRegions.length > 0 ||
    body.total_body_fat_percent != null ||
    muscle.lean_mass_index != null ||
    fmiValue !== null ||
    hasVisceralData ||
    regionRows.length > 0 ||
    compositionTableRows.length > 0 ||
    regionalFatRows.length > 0 ||
    armsLeft !== null ||
    heatmapImage != null ||
    bmdImage != null ||
    fmiImage != null ||
    leanVsFatImage != null;

  if (!hasResultsContent) {
    return (
      <div className='dexa-results-dashboard empty'>
        <p>No structured DEXA screen data found in this extraction.</p>
        <p className='hint'>Use “Update Extracted Data” to inspect or edit the raw JSON.</p>
      </div>
    );
  }

  return (
    <div className='dexa-results-dashboard'>
      <div className='dexa-results-header'>
        <h3>Your DEXA Scan results</h3>
        {(summary.scan_date || scanMeta.scan_date || summary.sex || patient.sex || summary.age_at_scan || patient.age_at_scan) && (
          <p className='dexa-results-meta'>
            {[
              summary.scan_date || scanMeta.scan_date,
              (summary.age_at_scan ?? patient.age_at_scan) != null
                ? `${summary.age_at_scan ?? patient.age_at_scan} yrs`
                : null,
              summary.sex || patient.sex
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        )}
      </div>

      {hasScanDetails && (
        <section className='dexa-card dexa-details-card'>
          <button
            type='button'
            className='dexa-accordion-header'
            onClick={() => setDetailsOpen((open) => !open)}
          >
            <span>Scan Details</span>
            <span className={`chevron ${detailsOpen ? 'open' : ''}`}>▾</span>
          </button>
          {detailsOpen && (
            <div className='dexa-details-list'>
              {scanDetails.heightCm != null && (
                <div className='detail-row'>
                  <span>Height</span>
                  <strong>{formatNumber(scanDetails.heightCm, 1)} cm</strong>
                </div>
              )}
              {scanDetails.weightKg != null && (
                <div className='detail-row'>
                  <span>Weight</span>
                  <strong>{formatNumber(scanDetails.weightKg, 1)} kg</strong>
                </div>
              )}
              {scanDetails.ethnicity && (
                <div className='detail-row'>
                  <span>Ethnicity</span>
                  <strong>{scanDetails.ethnicity}</strong>
                </div>
              )}
              {scanDetails.menopausalStatus && (
                <div className='detail-row'>
                  <span>Menopausal status</span>
                  <strong>{scanDetails.menopausalStatus}</strong>
                </div>
              )}
              {scanDetails.rmrKcal != null && (
                <div className='detail-row'>
                  <span>RMR</span>
                  <strong>{formatNumber(scanDetails.rmrKcal, 0)} kcal</strong>
                </div>
              )}
              {(scanDetails.facility || scanDetails.machine || scanDetails.software) && (
                <p className='detail-meta-line'>
                  {[scanDetails.facility, scanDetails.machine, scanDetails.software]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              )}
              {scanDetails.scanId && (
                <div className='detail-row detail-row--meta'>
                  <span>Scan ID</span>
                  <strong>{scanDetails.scanId}</strong>
                </div>
              )}
              {scanDetails.referencePopulation && (
                <div className='detail-row detail-row--meta'>
                  <span>Reference population</span>
                  <strong>{scanDetails.referencePopulation}</strong>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {bmiValue !== null && (
        <section className='dexa-card'>
          <div className='dexa-score-top'>
            <span className='dexa-score-title'>BMI</span>
            {bmiClassification && (
              <span className={`dexa-badge bmi-${bmiTone(bmiClassification)}`}>
                {String(bmiClassification).toUpperCase()}
              </span>
            )}
          </div>
          <div className='dexa-score-value'>{formatNumber(bmiValue, 1)}</div>
          <ScoreGauge
            value={bmiValue}
            min={BMI_GAUGE_MIN}
            max={BMI_GAUGE_MAX}
            segments={buildBmiGaugeSegments(bmiBands)}
            tickLabels={[
              { value: 18.5, label: '18.5' },
              { value: 25, label: '25' },
              { value: 30, label: '30' },
              { value: 40, label: '40' }
            ]}
          />
          {bmiCaveat && <p className='dexa-compare-text'>{bmiCaveat}</p>}
        </section>
      )}

      {(tScore !== null || zScore !== null) && (
        <section className='dexa-card'>
          {tScore !== null && (
            <div className='dexa-score-block'>
              <div className='dexa-score-top'>
                <span className='dexa-score-title'>Bone Density T-score</span>
                {tClass && (
                  <span className={`dexa-badge ${classificationTone(tClass)}`}>
                    {String(tClass).toUpperCase()}
                  </span>
                )}
              </div>
              <div className='dexa-score-value'>{formatSigned(tScore, 1)}</div>
              <ScoreGauge
                value={tScore}
                min={-5}
                max={1}
                segments={[
                  { label: 'Osteoporosis', from: -5, to: -2.5, color: '#e57373' },
                  { label: 'Osteopenia', from: -2.5, to: -1, color: '#ffb74d' },
                  { label: 'Normal', from: -1, to: 1, color: '#81c784' }
                ]}
                tickLabels={[
                  { value: -5, label: '-5' },
                  { value: -2.5, label: '-2.5' },
                  { value: -1, label: '-1' },
                  { value: 1, label: '+1' }
                ]}
              />
            </div>
          )}

          {zScore !== null && (
            <div className='dexa-score-block'>
              <div className='dexa-score-top'>
                <span className='dexa-score-title'>Bone Density Z-score</span>
              </div>
              <div className='dexa-score-value'>{formatSigned(zScore, 2)}</div>
              <ScoreGauge
                value={zScore}
                min={-4}
                max={4}
                segments={[
                  { label: 'Below Avg.', from: -4, to: -1, color: '#ef9a9a' },
                  { label: 'Avg. for your age', from: -1, to: 1, color: '#a5d6a7' },
                  { label: 'Above Avg.', from: 1, to: 4, color: '#66bb6a' }
                ]}
                tickLabels={[
                  { value: -4, label: '-4' },
                  { value: 0, label: '0' },
                  { value: 4, label: '+4' }
                ]}
              />
            </div>
          )}
        </section>
      )}

      {(boneRegions.length > 0 || bmdImage) && (
        <section className='dexa-card'>
          <button
            type='button'
            className='dexa-accordion-header'
            onClick={() => setBoneRegionsOpen((open) => !open)}
          >
            <span>Whole Body Bone Density</span>
            <span className={`chevron ${boneRegionsOpen ? 'open' : ''}`}>▾</span>
          </button>
          {boneRegionsOpen && (
            <div className={`dexa-bone-density-content ${bmdImage ? 'has-scan-image' : ''}`}>
              {bmdImage && (
                <div className='dexa-body-silhouette'>
                  <img
                    className='scan-image'
                    src={bmdImage.data_url}
                    alt='Bone density BMD scan'
                  />
                </div>
              )}
              {boneRegions.length > 0 && (
                <div className='dexa-table-wrap'>
                  <table className='dexa-data-table'>
                    <thead>
                      <tr>
                        <th>Region</th>
                        <th>Area (cm²)</th>
                        <th>BMC (g)</th>
                        <th>BMD (g/cm²)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {boneRegions.map((row) => (
                        <tr key={String(row.region)}>
                          <td>{row.region}</td>
                          <td>{formatNumber(row.area_cm2, 2) ?? '—'}</td>
                          <td>{formatNumber(row.bmc_g, 2) ?? '—'}</td>
                          <td>{formatNumber(row.bmd_g_cm2, 3) ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {sites.length > 0 && (
        <section className='dexa-card dexa-sites-card'>
          <button
            type='button'
            className='dexa-accordion-header'
            onClick={() => setSitesOpen((open) => !open)}
          >
            <span>Results by site</span>
            <span className={`chevron ${sitesOpen ? 'open' : ''}`}>▾</span>
          </button>
          {sitesOpen && (
            <div className='dexa-sites-list'>
              {sites.map((site) => {
                const value =
                  site.t_score != null
                    ? Number(site.t_score)
                    : site.value != null
                      ? Number(site.value)
                      : null;
                const tone = classificationTone(
                  site.classification || tScoreClassFromValue(value)
                );
                const hasScore = value !== null && !Number.isNaN(value);
                const bmd = site.bmd_g_cm2 != null ? Number(site.bmd_g_cm2) : null;
                return (
                  <div key={String(site.site)} className='dexa-site-row'>
                    <div className='dexa-site-left'>
                      <span className='dexa-site-name'>{site.site}</span>
                      {site.worst && <span className='dexa-worst-badge'>WORST</span>}
                    </div>
                    <div className='dexa-site-right'>
                      {hasScore ? (
                        <>
                          <span className={`dexa-site-value tone-${tone}`}>
                            <i className={`dot tone-${tone}`} />
                            {formatSigned(value, 1)}
                          </span>
                          <MiniSiteBar value={value} />
                        </>
                      ) : (
                        <span className='dexa-site-value bmd-only'>
                          {bmd !== null ? `${formatNumber(bmd, 3)} g/cm²` : '—'}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {(body.total_body_fat_percent != null ||
        body.lean_mass_percent != null ||
        body.visceral_fat_cm2 != null ||
        body.android_fat_percent != null ||
        body.gynoid_fat_percent != null ||
        heatmapImage) && (
        <section className='dexa-card dexa-body-comp-card'>
          <h4>Body Composition</h4>
          <div className={`dexa-body-comp-grid ${heatmapImage ? 'has-scan-image' : ''}`}>
            <div className='dexa-body-silhouette'>
              {heatmapImage ? (
                <img
                  className='scan-image'
                  src={heatmapImage.data_url}
                  alt='Body composition heatmap'
                />
              ) : (
                <>
                  <div className='silhouette' aria-hidden='true' />
                  <div className='silhouette-legend'>
                    <span>
                      <i className='low' /> Low
                    </span>
                    <span>
                      <i className='mid' /> Mid
                    </span>
                    <span>
                      <i className='high' /> High
                    </span>
                  </div>
                </>
              )}
            </div>
            <div className='dexa-body-metrics'>
              {body.total_body_fat_percent != null && (
                <div className='metric'>
                  <span className='label'>Total Body Fat</span>
                  <span className='value'>{formatNumber(body.total_body_fat_percent, 1)}%</span>
                  {body.total_body_fat_kg != null && (
                    <span className='sub'>{formatNumber(body.total_body_fat_kg, 1)} kg</span>
                  )}
                </div>
              )}
              {body.lean_mass_percent != null && (
                <div className='metric'>
                  <span className='label'>Lean Mass</span>
                  <span className='value'>{formatNumber(body.lean_mass_percent, 1)}%</span>
                  {body.lean_mass_kg != null && (
                    <span className='sub'>{formatNumber(body.lean_mass_kg, 1)} kg</span>
                  )}
                </div>
              )}
              {(body.visceral_fat_cm2 != null || body.visceral_fat_volume_cm3 != null) && (
                <div className='metric'>
                  <span className='label'>Visceral Fat</span>
                  <span className='value'>
                    {body.visceral_fat_cm2 != null
                      ? `${formatNumber(body.visceral_fat_cm2, 1)} cm²`
                      : `${formatNumber(body.visceral_fat_volume_cm3, 1)} cm³`}
                  </span>
                  <span className='sub'>Target &lt;{body.visceral_fat_target_cm2 || 100}</span>
                </div>
              )}
              {body.android_fat_percent != null && (
                <div className='metric'>
                  <span className='label'>Android Fat</span>
                  <span className='value'>{formatNumber(body.android_fat_percent, 1)}%</span>
                </div>
              )}
              {body.gynoid_fat_percent != null && (
                <div className='metric'>
                  <span className='label'>Gynoid Fat</span>
                  <span className='value'>{formatNumber(body.gynoid_fat_percent, 1)}%</span>
                </div>
              )}
              {body.android_to_gynoid_ratio != null && (
                <div className='metric'>
                  <span className='label'>A/G Ratio</span>
                  <span className='value'>{formatNumber(body.android_to_gynoid_ratio, 2)}</span>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {compositionTableRows.length > 0 && (
        <section className='dexa-card'>
          <button
            type='button'
            className='dexa-accordion-header'
            onClick={() => setCompositionOpen((open) => !open)}
          >
            <span>Body Composition Results</span>
            <span className={`chevron ${compositionOpen ? 'open' : ''}`}>▾</span>
          </button>
          {compositionOpen && (
            <div className='dexa-table-wrap'>
              <table className='dexa-data-table'>
                <thead>
                  <tr>
                    <th>Region</th>
                    <th>Lean Mass</th>
                    <th>% Lean</th>
                    <th>Fat Mass</th>
                    <th>% Fat</th>
                    <th>Bone Mass</th>
                    <th>Total Mass</th>
                  </tr>
                </thead>
                <tbody>
                  {compositionTableRows.map((row) => (
                    <tr key={row.key}>
                      <td>{row.label}</td>
                      <td>{row.leanMass != null ? `${formatNumber(row.leanMass, 3)} kg` : '—'}</td>
                      <td>{row.leanPercent != null ? `${formatNumber(row.leanPercent, 1)}%` : '—'}</td>
                      <td>{row.fatMass != null ? `${formatNumber(row.fatMass, 3)} kg` : '—'}</td>
                      <td>{row.fatPercent != null ? `${formatNumber(row.fatPercent, 1)}%` : '—'}</td>
                      <td>{row.boneMass != null ? `${formatNumber(row.boneMass, 2)} g` : '—'}</td>
                      <td>{row.totalMass != null ? `${formatNumber(row.totalMass, 3)} kg` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {compositionTableRows.length === 0 && regionalFatRows.length > 0 && (
        <section className='dexa-card'>
          <h4>Regional Fat %</h4>
          <div className='dexa-table-wrap'>
            <table className='dexa-data-table compact'>
              <thead>
                <tr>
                  <th>Region</th>
                  <th>% Fat</th>
                </tr>
              </thead>
              <tbody>
                {regionalFatRows.map((row) => (
                  <tr key={row.key}>
                    <td>{row.label}</td>
                    <td>{formatNumber(row.value, 1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {(muscle.lean_mass_index != null || muscle.almi != null) && (
        <section className='dexa-card'>
          <h4>Muscle Mass</h4>
          <div className='dexa-two-col'>
            {muscle.lean_mass_index != null && (
              <div className='dexa-stat'>
                <span className='label'>Lean Mass Index</span>
                <span className='value'>{formatNumber(muscle.lean_mass_index, 1)}</span>
                <span className='unit'>kg/m²</span>
              </div>
            )}
            {muscle.almi != null && (
              <div className='dexa-stat'>
                <span className='label'>Appendicular LMI</span>
                <span className='value'>{formatNumber(muscle.almi, 2)}</span>
                <span className='unit'>kg/m²</span>
              </div>
            )}
          </div>
        </section>
      )}

      {(fmiValue !== null || fmiImage) && (
        <section className='dexa-card'>
          <h4>Fat Mass Index (FMI)</h4>
          <p className='dexa-card-sub'>{fmi.description || 'Your fat relative to height'}</p>
          {fmiImage && (
            <div className='dexa-body-silhouette'>
              <img className='scan-image' src={fmiImage.data_url} alt='Fat mass index scan' />
            </div>
          )}
          {fmiValue !== null && (
          <div className='dexa-large-metric'>
            {formatNumber(fmiValue, 2)} <span>kg/m²</span>
          </div>
          )}
          <ScoreGauge
            value={fmiValue}
            min={0}
            max={16}
            segments={[
              { label: 'Below', from: 0, to: 5, color: '#64b5f6' },
              { label: 'Normal', from: 5, to: 9, color: '#81c784' },
              { label: 'Overfat', from: 9, to: 12, color: '#ffd54f' },
              { label: 'Obese', from: 12, to: 16, color: '#e57373' }
            ]}
          />
          {(fmi.age_at_scan != null || fmi.sex || fmi.percentile != null || fmi.classification) && (
            <p className='dexa-compare-text'>
              {[
                fmi.percentile != null ? `Percentile ${formatNumber(fmi.percentile, 0)}` : null,
                fmi.classification ? String(fmi.classification) : null,
                (fmi.age_at_scan != null || fmi.sex)
                  ? `Compared to other ${[fmi.age_at_scan, 'year old', fmi.sex].filter(Boolean).join(' ')}`
                  : null
              ].filter(Boolean).join(' · ')}
              {'. '}
              Normal range ~5–9 kg/m²
            </p>
          )}
        </section>
      )}

      {hasVisceralData && (
        <section className='dexa-card'>
          <h4>Visceral Fat</h4>
          <p className='dexa-card-sub'>Fat around internal organs</p>
          {visceralValue != null && (
            <div className='dexa-large-metric'>
              {formatNumber(visceralValue, 1)} <span>{visceralUnit}</span>
            </div>
          )}
          <div className='dexa-details-list visceral-details'>
            {visceralArea != null && (
              <div className='detail-row'>
                <span>Area</span>
                <strong>{formatNumber(visceralArea, 1)} cm²</strong>
              </div>
            )}
            {visceralVolume != null && (
              <div className='detail-row'>
                <span>Volume</span>
                <strong>{formatNumber(visceralVolume, 1)} cm³</strong>
              </div>
            )}
            {visceralMass != null && (
              <div className='detail-row'>
                <span>Mass</span>
                <strong>{formatNumber(visceralMass, 1)} g</strong>
              </div>
            )}
            <div className='detail-row'>
              <span>Target</span>
              <strong>&lt;{formatNumber(visceralTarget, 0)} cm²</strong>
            </div>
            {visceral.classification && (
              <div className='detail-row'>
                <span>Classification</span>
                <strong>{String(visceral.classification)}</strong>
              </div>
            )}
            {visceral.percentile != null && (
              <div className='detail-row'>
                <span>Percentile</span>
                <strong>{formatNumber(visceral.percentile, 0)}</strong>
              </div>
            )}
            {(visceral.sex || visceral.age_at_scan != null) && (
              <div className='detail-row'>
                <span>Reference</span>
                <strong>
                  {[visceral.age_at_scan != null ? `${visceral.age_at_scan} yrs` : null, visceral.sex]
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </strong>
              </div>
            )}
          </div>
          {visceral.finding && <p className='dexa-finding'>{visceral.finding}</p>}
          {visceralArea != null && (
            <ScoreGauge
              value={visceralArea}
              min={0}
              max={200}
              segments={[
                { label: 'Normal', from: 0, to: 100, color: '#81c784' },
                { label: 'Increased', from: 100, to: 160, color: '#ffd54f' },
                { label: 'High', from: 160, to: 200, color: '#e57373' }
              ]}
              tickLabels={[
                { value: 20, label: '20' },
                { value: 100, label: '100' },
                { value: 160, label: '160' }
              ]}
            />
          )}
        </section>
      )}

      {(regionRows.length > 0 || leanVsFatImage) && (
        <section className='dexa-card'>
          <div className='dexa-card-title-row'>
            <h4>Lean vs Fat by Region</h4>
            <div className='dexa-legend'>
              <span>
                <i className='lean' /> Lean Mass
              </span>
              <span>
                <i className='fat' /> Fat Mass
              </span>
            </div>
          </div>
          {leanVsFatImage && (
            <div className='dexa-body-silhouette'>
              <img className='scan-image' src={leanVsFatImage.data_url} alt='Lean versus fat by region' />
            </div>
          )}
          <div className='dexa-region-chart'>
            {regionRows.map((row) => (
              <div key={row.key} className='dexa-region-col'>
                <div className='bars' style={{ height: '160px' }}>
                  <div
                    className='stack'
                    style={{ height: `${((row.lean + row.fat) / maxRegionTotal) * 100}%` }}
                  >
                    <div
                      className='fat'
                      style={{ height: `${(row.fat / (row.lean + row.fat || 1)) * 100}%` }}
                      title={`Fat ${formatNumber(row.fat, 1)} kg`}
                    />
                    <div
                      className='lean'
                      style={{ height: `${(row.lean / (row.lean + row.fat || 1)) * 100}%` }}
                      title={`Lean ${formatNumber(row.lean, 1)} kg`}
                    />
                  </div>
                </div>
                <span className='region-label'>{row.label}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {(armsLeft !== null || legsLeft !== null) && (
        <section className='dexa-card'>
          <div className='dexa-card-title-row'>
            <h4>Left/Right Muscle Symmetry</h4>
            <div className='dexa-legend'>
              <span>
                <i className='left' /> Left
              </span>
              <span>
                <i className='right' /> Right
              </span>
            </div>
          </div>

          {armsLeft !== null && armsRight !== null && (
            <div className='dexa-symmetry-row'>
              <span className='limb-label'>Arms</span>
              <div className='symmetry-bars'>
                <div className='bar-row'>
                  <span className='side-value'>{formatNumber(armsLeft, 2)} kg</span>
                  <div className='bar-track'>
                    <div
                      className='bar left'
                      style={{ width: `${(armsLeft / maxLimb) * 100}%` }}
                    />
                  </div>
                </div>
                <div className='bar-row'>
                  <div className='bar-track'>
                    <div
                      className='bar right'
                      style={{ width: `${(armsRight / maxLimb) * 100}%` }}
                    />
                  </div>
                  <span className='side-value'>{formatNumber(armsRight, 2)} kg</span>
                </div>
              </div>
            </div>
          )}

          {legsLeft !== null && legsRight !== null && (
            <div className='dexa-symmetry-row'>
              <span className='limb-label'>Legs</span>
              <div className='symmetry-bars'>
                <div className='bar-row'>
                  <span className='side-value'>{formatNumber(legsLeft, 2)} kg</span>
                  <div className='bar-track'>
                    <div
                      className='bar left'
                      style={{ width: `${(legsLeft / maxLimb) * 100}%` }}
                    />
                  </div>
                </div>
                <div className='bar-row'>
                  <div className='bar-track'>
                    <div
                      className='bar right'
                      style={{ width: `${(legsRight / maxLimb) * 100}%` }}
                    />
                  </div>
                  <span className='side-value'>{formatNumber(legsRight, 2)} kg</span>
                </div>
              </div>
            </div>
          )}

          {symmetry.imbalance_percent != null && (
            <p className='dexa-imbalance'>
              Imbalance: {formatNumber(symmetry.imbalance_percent, 1)}%
            </p>
          )}
        </section>
      )}
    </div>
  );
};

export default DexaResultsDashboard;
